import {
  type AccessPolicy,
  getResourceString as baseGetResourceString,
  type InterpolationVariables,
  validateAction
} from '@takeshape/actions';
import type { AccessPolicyDocument } from '@takeshape/schema';
import isArray from 'lodash/isArray';
import PBAC from 'pbac';

export type AccessPolicyParams = {
  resource: string;
  action: string;
  context?: Record<string, string>;
};

export function getAccessPolicy(policy: AccessPolicyDocument | AccessPolicyDocument[]): AccessPolicy {
  const pbac = new PBAC(policy);
  const accessPolicy = (params: AccessPolicyParams): boolean => {
    return Boolean(pbac.evaluate(params));
  };

  accessPolicy.policies = isArray(policy) ? policy : [policy];
  return accessPolicy;
}

export const getActionNamespace = (action: string): string => action.split(':')[0];
export const getResourceNamespace = (resource: string): string => resource?.split(':')[1];

export function checkAccessPolicy(accessPolicy: AccessPolicy, action: string, resource: string): boolean {
  if (!validateAction(action)) {
    throw new Error(`Unknown action "${action}"`);
  }

  const namespace = getActionNamespace(action);
  const resourceNamespace = getResourceNamespace(resource);
  if (namespace !== resourceNamespace) {
    throw new Error(`Action "${action}" does not match the resource namespace ${resource}`);
  }

  return Boolean(accessPolicy?.({ action, resource }));
}

export function getResourceString(namespace: string, options?: InterpolationVariables): string {
  return baseGetResourceString(namespace, {
    projectId: '{projectId}',
    ...options
  });
}

export function formatContentTypeResource(shapeId?: string): string {
  return getResourceString('content-type', { workspace: 'default', shapeId });
}

export function formatLockResource(projectId: string, shapeId: string, contentId: string): string {
  return `ts:lock:${projectId}:${shapeId}/${contentId}`;
}

export const PROJECT_RESOURCE = 'ts:project:{projectId}';
